import type { ColDef, ColGroupDef, ExcelStyle } from "ag-grid-enterprise";
import { excelStyles } from "components/excel-ag-grid-export/excel-style";
import { DateUtils, DATE_FORMAT_MMDDYYYY } from "utils/dateutils";

// Reuses the ids the existing Excel export already defines, so risk colours stay in one place.
export const RISK_EXCEL_CLASS: Record<string, string> = {
  HIGH: "highRisk",
  MEDIUM: "mediumRisk",
  LOW: "lowRisk"
};

// Same hexes as the on-screen risk tag and the standard Excel export. Low is blue, not green.
const RISK_TEXT_COLOUR: Record<string, string> = {
  HIGH: "#cf1322",
  MEDIUM: "#d46b08",
  LOW: "#0958d9"
};

const formatDate = (params: { value: unknown }): string =>
  params.value ? DateUtils.format(params.value, DATE_FORMAT_MMDDYYYY) : "";

const dateColumn = (field: string, headerName: string): ColDef => ({
  colId: field,
  field,
  headerName,
  width: 115,
  minWidth: 105,
  valueFormatter: formatDate,
  filter: "agDateColumnFilter",
  wrapHeaderText: true,
  autoHeaderHeight: true
});

const dateBand = (
  headerName: string,
  fields: { field: string; label: string }[]
): ColGroupDef => ({
  headerName,
  groupId: `band-${headerName.replace(/\s+/g, "-").toLowerCase()}`,
  marryChildren: true,
  children: fields.map((entry) => dateColumn(entry.field, entry.label))
});

const REQUIRED_PROJECTED_ACTUAL = (prefix: string) => [
  { field: `${prefix}Required`, label: "Required" },
  { field: `${prefix}Projected`, label: "Projected" },
  { field: `${prefix}Actual`, label: "Actual" }
];

/**
 * Fixed layout for the Davis custom export, mirroring the agreed material-to-grid mapping.
 * Deliberately hardcoded: the configurable editor is a later, separate piece of work.
 */
export const buildCustomExportColumnDefs = (): (ColDef | ColGroupDef)[] => [
  {
    colId: "materialName",
    field: "materialName",
    headerName: "Material Name",
    pinned: "left",
    width: 240,
    minWidth: 180,
    wrapHeaderText: true,
    autoHeaderHeight: true
  },
  {
    colId: "subcontractor",
    field: "subcontractor",
    headerName: "Sub Contractor",
    width: 210,
    wrapHeaderText: true,
    autoHeaderHeight: true
  },
  {
    colId: "risk",
    field: "risk",
    headerName: "Risk",
    width: 95,
    wrapHeaderText: true,
    autoHeaderHeight: true,
    cellStyle: (params: { value: unknown }) => {
      const colour = RISK_TEXT_COLOUR[String(params.value ?? "").toUpperCase()];
      return colour ? { color: colour, fontWeight: "700" } : null;
    },
    // cellClass drives the Excel styling; cellStyle alone is not carried into the workbook.
    cellClass: (params: { value: unknown }) => [
      "bodyCell",
      RISK_EXCEL_CLASS[String(params.value ?? "").toUpperCase()] ?? ""
    ]
  },
  {
    colId: "actualFloat",
    field: "actualFloat",
    headerName: "Actual Float",
    width: 115,
    type: "numericColumn",
    filter: "agNumberColumnFilter",
    wrapHeaderText: true,
    autoHeaderHeight: true
  },
  {
    colId: "linkedSubmittalCount",
    field: "linkedSubmittalCount",
    headerName: "Number of Linked Submittals",
    width: 135,
    type: "numericColumn",
    filter: "agNumberColumnFilter",
    wrapHeaderText: true,
    autoHeaderHeight: true
  },
  dateBand("Submitted by SC", REQUIRED_PROJECTED_ACTUAL("submittedBySC")),
  dateBand(
    "Submitted for Design Review",
    REQUIRED_PROJECTED_ACTUAL("submittedForDesignReview")
  ),
  dateBand(
    "Design Review Complete",
    REQUIRED_PROJECTED_ACTUAL("designReviewComplete")
  ),
  dateBand(
    "Submittal Distributed",
    REQUIRED_PROJECTED_ACTUAL("submittalDistributed")
  ),
  dateBand("Material Released", REQUIRED_PROJECTED_ACTUAL("materialRelease")),
  dateBand("Material Shipped", [
    { field: "materialShipActual", label: "Actual" }
  ]),
  {
    colId: "totalLeadTime",
    field: "totalLeadTime",
    headerName: "Total Lead Time",
    width: 125,
    type: "numericColumn",
    filter: "agNumberColumnFilter",
    wrapHeaderText: true,
    autoHeaderHeight: true
  },
  dateBand("Material Delivered", REQUIRED_PROJECTED_ACTUAL("materialDelivery")),
  {
    colId: "governingActivityName",
    field: "governingActivityName",
    headerName: "Governing Activity",
    width: 240,
    minWidth: 180,
    wrapHeaderText: true,
    autoHeaderHeight: true
  }
];

export const CUSTOM_EXPORT_DEFAULT_COL_DEF: ColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  suppressHeaderMenuButton: true,
  cellClass: "bodyCell"
};

const BORDER = {
  lineStyle: "Continuous" as const,
  weight: 1 as const,
  color: "#BFBFBF"
};

const ALL_BORDERS = {
  borderTop: BORDER,
  borderRight: BORDER,
  borderBottom: BORDER,
  borderLeft: BORDER
};

/**
 * Keeps the shared risk/cell styles but replaces "header" and adds "headerGroup", because the
 * standard styles carry no borders and AG Grid styles the grouped header row separately.
 */
export const CUSTOM_EXPORT_EXCEL_STYLES: ExcelStyle[] = [
  ...(excelStyles as ExcelStyle[]).filter((style) => style.id !== "header"),
  {
    id: "header",
    alignment: { vertical: "Center", horizontal: "Center", wrapText: true },
    font: { size: 12, bold: true, color: "#262626" },
    interior: { color: "#F0F0F0", pattern: "Solid" },
    borders: ALL_BORDERS
  },
  {
    id: "headerGroup",
    alignment: { vertical: "Center", horizontal: "Center" },
    font: { size: 12, bold: true, color: "#262626" },
    interior: { color: "#DDE3EA", pattern: "Solid" },
    borders: ALL_BORDERS
  },
  {
    id: "bodyCell",
    alignment: { vertical: "Top" },
    borders: ALL_BORDERS
  }
];

/** Number of leaf columns, used to merge the title row across the full width. */
export const LEAF_COLUMN_COUNT = buildCustomExportColumnDefs().reduce(
  (total, def) =>
    total + ("children" in def && def.children ? def.children.length : 1),
  0
);
